// src/components/footerVenta.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import ContextStates from "../context/ContextStates";

// ===== Helpers =====
const num = (v) => (typeof v === "number" ? v : Number.parseFloat(String(v || 0))) || 0;

const buildProductMaps = (productos = []) => {
    const byId = {};
    const byCode = {};
    for (const p of productos) {
        byId[p.id] = p;
        byCode[String(p.codigo)] = p;
    }
    return { byId, byCode };
};

// Suma cantidades por numerocombo (A+B+...)
const buildQtyByCombo = (items = [], productById = {}, productByCode = {}) => {
    const map = {};
    for (const it of items) {
        const master =
            (it.id && productById[it.id]) ||
            (it.codigo && productByCode[String(it.codigo)]) ||
            null;
        const nc = master?.numerocombo;
        if (!nc) continue;
        map[nc] = (map[nc] || 0) + (Number(it.cantidad) || 0);
    }
    return map;
};

// Unitario POR PRODUCTO usando los tiers del combo de ese producto
const unitFromProductComboTiers = (base, productComboTiers, groupQty) => {
    let unit = num(base);
    const tiers = Array.isArray(productComboTiers)
        ? productComboTiers.slice().sort((a, b) => a.cantidadmin - b.cantidadmin)
        : [];
    for (const t of tiers) {
        if (groupQty >= Number(t.cantidadmin)) unit = num(t.precio_unitario);
        else break;
    }
    return unit;
};

const unitByCombo = (masterProduct, selfQty, qtyByCombo) => {
    const base = num(masterProduct?.precio ?? masterProduct?.precio_base ?? 0);
    if (masterProduct?.numerocombo && masterProduct?.precios_combo?.length) {
        const groupQty = qtyByCombo[masterProduct.numerocombo] || 0;
        return unitFromProductComboTiers(base, masterProduct.precios_combo, groupQty);
    }
    return base; // sin combo: base
};

const subtotalByCombo = (masterProduct, qty, qtyByCombo) => {
    const q = Number(qty) || 0;
    if (q <= 0) return 0;
    const unit = unitByCombo(masterProduct, q, qtyByCombo);
    return unit * q;
};

const FooterVenta = () => {
    const [total, setTotal] = useState(0);

    // del contexto: traemos venta y productos para poder mapear combos
    const { venta = [], productos = [], setFormView, setViewOpen } = useContext(ContextStates);

    // Mapas maestro de productos para ubicar numerocombo y precios_combo
    const { byId: productById, byCode: productByCode } = useMemo(
        () => buildProductMaps(productos),
        [productos]
    );

    // Cantidades por combo (A+B+...)
    const qtyByCombo = useMemo(
        () => buildQtyByCombo(venta, productById, productByCode),
        [venta, productById, productByCode]
    );

    // Recalcular total con lógica de combos; si algún renglón no está en el maestro, fallback a sus propios campos
    useEffect(() => {
        let acc = 0;
        for (const it of venta) {
            const master =
                (it.id && productById[it.id]) ||
                (it.codigo && productByCode[String(it.codigo)]) ||
                null;

            if (master) {
                acc += subtotalByCombo(master, it.cantidad, qtyByCombo);
            } else {
                // Fallback (si por alguna razón no encontramos el maestro)
                const base =
                    num(it.preciopromocion && (it.tipopromo === "1" || it.tipopromo === "2"))
                        ? it.preciopromocion
                        : it.precio;
                acc += num(base) * num(it.cantidad);
            }
        }
        setTotal(acc);
    }, [venta, qtyByCombo, productById, productByCode]);

    const finalizarVenta = () => {
        if (total > 0) {
            setFormView(2);
            setViewOpen(true);
        }
    };

    return (
        <div className="w-full mt-auto md:h-20 mx-auto mb-20 bg-gray-200">
            <div className="w-10/12 mx-auto flex">
                <div className="w-2/3">
                    <div className="w-full md:w-1/2 ml-auto p-5">
                        <p className="text-center text-4xl font-light">
                            Total: ${total.toLocaleString("es-AR", { maximumFractionDigits: 2 })}
                        </p>
                    </div>
                </div>
                <div className="w-2/3 md:w-1/3">
                    <div className="w-full md:w-1/2 p-4 ml-auto">
                        <button
                            type="button"
                            className={`w-full p-3 text-white uppercase font-semibold rounded ${total > 0 ? "bg-orange-500" : "bg-orange-200"
                                }`}
                            onClick={finalizarVenta}
                            disabled={total <= 0}
                        >
                            Finalizar venta
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FooterVenta;
